// Generic utils
import _ from 'lodash';

function messageToConversation(message) {
    let bodyHtml = (() => {
        if (!message.payload?.parts && !message.payload?.body) return message.body?.html;
        const part = (() => {
            if (message.payload.parts?.[0]?.mimeType === 'multipart/alternative') {
                return message.payload.parts[0].parts?.find(p => p.mimeType === 'text/html');
            }
            if (message.payload.parts?.[0]?.mimeType === 'multipart/related') {
                return message.payload.parts[0].parts?.find(p => p.mimeType === 'text/html');
            }
            return message.payload.parts?.find(p => p.mimeType === 'text/html');
        })();
        const data = part ? part.body?.data : message.payload.body?.data;
        if (!data) return '';
        return part ? decodeGmailHtml(part) : atob(data.replace(/-/g, '+').replace(/_/g, '/').replace(/=/g, ''));
    })();

    const bodyPlain = (() => {
        const part = (() => {
            if (message.payload.parts?.[0]?.mimeType === 'multipart/alternative') {
                return message.payload.parts[0].parts?.find(p => p.mimeType === 'text/plain');
            }
            return message.payload.parts?.find(p => p.mimeType === 'text/plain');
        })();
        let data = part ? part.body?.data : message.payload.body?.data;
        if (!data) {
            data = bodyHtml || '';
            data = data.replace(/<style([\s\S]*?)<\/style>/gi, '');
            data = data.replace(/<script([\s\S]*?)<\/script>/gi, '');
            data = data.replace(/<\/div>/ig, '\n');
            data = data.replace(/<\/li>/ig, '\n');
            data = data.replace(/<li>/ig, '  *  ');
            data = data.replace(/<\/ul>/ig, '\n');
            data = data.replace(/<\/p>/ig, '\n');
            data = data.replace(/<br\s*[\/]?>/gi, '\n');
            data = data.replace(/<[^>]+>/ig, '');
            return data;
        } else {
            return atob(data.replace(/-/g, '+').replace(/_/g, '/').replace(/=/g, ''));
        }
    })();

    const hasAttachments = (() => {
        // Check for attachments - can be detected by attachmentId or filename presence (for metadata format)
        const checkPart = (p) => {
            // If body.attachmentId exists, it's definitely an attachment
            if (p.body?.attachmentId) return true;
            // If filename exists and it's not a text/html, text/plain, or multipart part, it's likely an attachment
            if (p.filename && p.mimeType && !p.mimeType.startsWith('text/') && !p.mimeType.startsWith('multipart/')) {
                return true;
            }
            // Recursively check nested parts (for multipart/alternative, multipart/mixed, etc.)
            if (p.parts && Array.isArray(p.parts)) {
                return p.parts.some(checkPart);
            }
            return false;
        };
        // Check all parts recursively
        return message.payload.parts?.some(checkPart) || false;
    })();

    const attachments = (() => {
        // Helper to check if a part is an attachment
        const isAttachment = (p) => {
            if (p.body?.attachmentId) return true;
            if (p.filename && p.mimeType && !p.mimeType.startsWith('text/') && !p.mimeType.startsWith('multipart/')) {
                return true;
            }
            return false;
        };
        
        // Helper to build attachment object
        const buildAttachment = (p) => ({
            id: p.body?.attachmentId || p.filename || `attachment-${Math.random()}`,
            name: p.filename,
            size: p.body?.size,
            contentType: p.mimeType,
            inlineId: p.headers?.find(h => h.name.toUpperCase() === 'CONTENT-ID')?.value?.replace(/[<>]/g, '')
        });
        
        // Recursively collect attachments from all parts
        const collectAttachments = (parts) => {
            if (!parts || !Array.isArray(parts)) return [];
            const result = [];
            for (const part of parts) {
                if (isAttachment(part)) {
                    result.push(buildAttachment(part));
                }
                // Recursively check nested parts
                if (part.parts && Array.isArray(part.parts)) {
                    result.push(...collectAttachments(part.parts));
                }
            }
            return result;
        };
        
        return collectAttachments(message.payload.parts);
    })();

    return {
        origin: message,
        id: message.id,
        conversationId: message.threadId,
        globalMessageId: encodeURIComponent(message.payload.headers.find(h => h.name === 'Message-ID')?.value || ''),
        receivedDateTime: new Date(parseInt(message.internalDate)).toISOString(),
        subject: message.payload.headers.find(h => h.name === 'Subject')?.value,
        unread: message.labelIds?.includes('UNREAD'),
        flagged: message.labelIds?.includes('STARRED'),
        snippet: message.snippet || null,
        body: {
            content: bodyPlain,
            html: bodyHtml
        },
        sender: (() => {
            const from = message.payload.headers.find(h => h.name === 'From')?.value;
            if (from.includes('<')) return {   
                emailAddress: {
                    name: from.split('<')[0]?.trim() || '',
                    address: from.split('<')[1]?.trim().replace('>', '') || ''
                }
            }; else return { emailAddress: { name: '', address: from } }
        })(),
        toRecipients: message.payload.headers.find(h => h.name === 'To')?.value.split(',').map(r => {
            if (r.includes('<')) {
                return {
                    emailAddress: {
                        name: r.split('<')[0]?.trim(),
                        address: r.split('<')[1]?.trim().replace('>', '')
                    }
                }
            } else {
                return {
                    emailAddress: { name: '', address: r }
                }
            }
        }),
        ccRecipients: _.compact((message.payload.headers.find(h => h.name === 'Cc')?.value?.split(',') || []).map(r => {
            if (!r) return null;
            if (r.includes('<')) {
                return {
                    emailAddress: {
                        name: r.split('<')[0]?.trim(),
                        address: r.split('<')[1]?.trim().replace('>', '')
                    }
                }
            } else {
                return {
                    emailAddress: { name: '', address: r }
                }
            }
        })),
        hasAttachments,
        attachments,
    }
}

function ensureCrLf(s) {
    return s.replace(/\r?\n/g, '\r\n');
}

function foldHeaderValue(value) {
    return value.replace(/\r?\n/g, ' ');
}

function encodeFilenameParam(filename) {
    return `filename*=${"utf-8"}''${encodeURIComponent(filename)}`;
}

function wrapBase64(b64) {
    const chunk = 76;
    let out = '';
    for (let i = 0; i < b64.length; i += chunk) out += b64.slice(i, i + chunk) + '\r\n';
    return out;
}

function b64urlToUint8Array(b64url) {
    // Base64url -> Uint8Array
    const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
    const b64 = (b64url.replace(/-/g, '+').replace(/_/g, '/')) + pad;
    const str = atob(b64);
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i);
    return bytes;
}

function b64urlDecodeToString(b64url) {
    try {
        const bytes = b64urlToUint8Array(b64url);
        // decode UTF-8
        const dec = new TextDecoder('utf-8');
        return dec.decode(bytes);
    } catch {
        return '';
    }
}

function htmlEscape(str) {
    return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function plainToHtml(plain) {
    return htmlEscape(plain).replace(/\r?\n/g, '<br>');
}

function findPartByMime(parts, mime) {
    if (!parts) return null;
    for (const p of parts) {
        if (p.mimeType === mime && p.body?.data) return p;
        const found = findPartByMime(p.parts, mime);
        if (found) return found;
    }
    return null;
}

function getMessageHtmlFromPayload(payload) {
    if (!payload) return '';
    // Prefer text/html; fall back to text/plain
    if (payload.mimeType === 'text/html' && payload.body?.data) {
        return b64urlDecodeToString(payload.body.data);
    }
    if (payload.mimeType === 'text/plain' && payload.body?.data) {
        return plainToHtml(b64urlDecodeToString(payload.body.data));
    }
    // multipart: search for html then plain
    const htmlPart = findPartByMime(payload.parts, 'text/html');
    if (htmlPart) return b64urlDecodeToString(htmlPart.body.data);
    const txtPart = findPartByMime(payload.parts, 'text/plain');
    if (txtPart) return plainToHtml(b64urlDecodeToString(txtPart.body.data));
    // fallback: maybe body.data exists at top
    if (payload.body?.data) return plainToHtml(b64urlDecodeToString(payload.body.data));
    return '';
}

function formatQuotedHeader({ from, date }) {
    // Typical email clients format: On <DATE>, <NAME/ADDR> wrote:
    // We use raw header values to stay simple.
    const d = date ? new Date(date) : null;
    const dateStr = d && !isNaN(d) ? d.toString() : (date || '');
    return `On ${htmlEscape(dateStr)}, ${htmlEscape(from || '')} wrote:`;
}

function buildRfc822MultipartMixed({ from, to, cc, subject, inReplyTo, references, html, attachments = [] }) {
    const mixedBoundary = 'mixed_' + Math.random().toString(36).slice(2);

    const headers = [];
    if (from) headers.push(`From: ${foldHeaderValue(from)}`);
    if (to) headers.push(`To: ${foldHeaderValue(to)}`);
    if (cc) headers.push(`Cc: ${foldHeaderValue(cc)}`);
    headers.push(`Subject: ${foldHeaderValue(subject)}`);
    if (inReplyTo) headers.push(`In-Reply-To: ${inReplyTo}`);
    if (references) headers.push(`References: ${references}`);
    headers.push('MIME-Version: 1.0');
    headers.push(`Content-Type: multipart/mixed; boundary="${mixedBoundary}"`);

    const parts = [];

    // body (HTML)
    parts.push(
        `--${mixedBoundary}\r\n` +
        `Content-Type: text/html; charset=UTF-8\r\n` +
        `Content-Transfer-Encoding: 8bit\r\n\r\n` +
        html + `\r\n`
    );

    // attachments
    for (const att of (attachments || [])) {
        const name = att.name || 'attachment';
        const type = att.type || 'application/octet-stream';
        const fnParam = encodeFilenameParam(name);
        parts.push(
            `--${mixedBoundary}\r\n` +
            `Content-Type: ${type}; name="${name.replace(/"/g, '\\"')}"\r\n` +
            `Content-Transfer-Encoding: base64\r\n` +
            `Content-Disposition: attachment; ${fnParam}\r\n\r\n` +
            wrapBase64(att.data || '') + `\r\n`
        );
    }

    parts.push(`--${mixedBoundary}--`);
    const rfc822 = headers.join('\r\n') + '\r\n\r\n' + parts.join('');
    return ensureCrLf(rfc822);
}

function normalizeCharset(cs = 'utf-8') {
    cs = cs.trim().toLowerCase();
    if (cs === 'utf8') return 'utf-8';
    if (cs === 'us-ascii' || cs === 'ascii') return 'utf-8'; // safe superset
    if (cs === 'iso8859-1') return 'iso-8859-1';
    return cs;
  }
  
  function base64urlToBytes(b64url) {
    let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    // strip whitespace just in case
    b64 = b64.replace(/\s+/g, '');
    // pad to length % 4 === 0
    while (b64.length % 4) b64 += '=';
    const bin = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i) & 0xFF;
    return bytes;
  }
  
  // Pure byte-level quoted-printable decoder -> Uint8Array
  function qpDecodeBytes(inputBytes) {
    const out = [];
    for (let i = 0; i < inputBytes.length; i++) {
      const c = inputBytes[i];
      if (c === 0x3D /* '=' */) {
        // soft line break "=\r\n" or "=\n"
        const n1 = inputBytes[i + 1], n2 = inputBytes[i + 2];
        if (n1 === 0x0D && n2 === 0x0A) { i += 2; continue; }
        if (n1 === 0x0A) { i += 1; continue; }
        // hex octet =HH
        const h1 = inputBytes[i + 1], h2 = inputBytes[i + 2];
        const hex = ((h1 >= 0x30 && h1 <= 0x39) || (h1 | 0x20) >= 0x61 && (h1 | 0x20) <= 0x66) &&
                    ((h2 >= 0x30 && h2 <= 0x39) || (h2 | 0x20) >= 0x61 && (h2 | 0x20) <= 0x66);
        if (hex) {
          const v = parseInt(String.fromCharCode(h1, h2), 16);
          out.push(v);
          i += 2;
          continue;
        }
        // malformed "=", keep literal
        out.push(c);
      } else {
        out.push(c);
      }
    }
    return new Uint8Array(out);
  }
  
  function parseMetaCharsetFromHtml(htmlString) {
    const m = /<meta[^>]+charset\s*=\s*["']?([\w-]+)["']?/i.exec(htmlString);
    return m ? normalizeCharset(m[1]) : null;
  }
  
  function getHeader(headers, name) {
    const n = name.toLowerCase();
    const h = headers?.find(h => String(h.name || '').toLowerCase() === n);
    return h?.value || '';
  }
  
  function decodeGmailHtml(part) {
    const ct = getHeader(part.headers, 'Content-Type') || '';
    const m = ct.match(/charset="?([\w-]+)"?/i);
    let charset = normalizeCharset(m?.[1] || 'utf-8');
  
    const transferEnc = normalizeCTE(getHeader(part.headers, 'Content-Transfer-Encoding'));
    function normalizeCTE(v) { return (v || '').trim().toLowerCase(); }
  
    // 1) Get raw bytes of the *encoded* body (Gmail gives base64url)
    if (!part.body || (!part.body.data && !part.body.attachmentId)) return '';
  
    let bytes = base64urlToBytes(part.body.data);
  
    // 2) If quoted-printable, decode QP at the **byte level**
    if (transferEnc === 'quoted-printable') {
      bytes = qpDecodeBytes(bytes);
    } else if (transferEnc === 'base64') {
      // already handled by base64urlToBytes()
    } // else '7bit'/'8bit'/'binary': bytes are already raw
  
    // 3) Decode bytes -> string with the (normalized) charset
    let html = new TextDecoder(charset, { fatal: false }).decode(bytes);
  
    // 4) If charset was missing/wrong, try meta charset inside the HTML
    if (/utf-8|utf8/.test(charset) === false) {
      const metaCs = parseMetaCharsetFromHtml(html);
      if (metaCs && metaCs !== charset) {
        charset = metaCs;
        html = new TextDecoder(charset, { fatal: false }).decode(bytes);
      }
    }
  
    // 5) Heuristic repair for classic mojibake (UTF-8 read as Latin-1)
    // Detect common "â€™" style sequences; if present, reinterpret as Latin-1 -> UTF-8.
    if (/â[\u0080-\u00BF]/.test(html)) {
      const reBytes = Uint8Array.from(Array.from(html, ch => ch.charCodeAt(0) & 0xFF));
      try {
        const repaired = new TextDecoder('utf-8', { fatal: true }).decode(reBytes);
        html = repaired;
      } catch {
        // leave as-is if repair fails
      }
    }
  
    return html;
  }


export { messageToConversation, getMessageHtmlFromPayload, buildRfc822MultipartMixed, formatQuotedHeader };